import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, MapPin, Utensils } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrderType, DeliveryZone } from '@/types/index';

interface CartFormProps {
  orderType: OrderType;
  setOrderType: (type: OrderType) => void;
  isTableFromQR: boolean;
  customerInfo: {
    name: string;
    phone: string;
    address: string;
    tableNumber: string;
    notes: string;
  };
  setCustomerInfo: React.Dispatch<React.SetStateAction<{
    name: string;
    phone: string;
    address: string;
    tableNumber: string;
    notes: string;
  }>>;
  deliveryZones: DeliveryZone[];
  selectedDeliveryZone: DeliveryZone | null;
  setSelectedDeliveryZone: (zone: DeliveryZone | null) => void;
  zonesLoading: boolean;
  currency: string;
  totalAmount: number;
}

export const CartForm: React.FC<CartFormProps> = ({
  orderType,
  setOrderType,
  isTableFromQR,
  customerInfo,
  setCustomerInfo,
  deliveryZones,
  selectedDeliveryZone,
  setSelectedDeliveryZone,
  zonesLoading,
  currency,
  totalAmount,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
      {/* Order Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-bold text-gray-900">{t('cart.order_type', 'طريقة الاستلام')}</Label>
        {isTableFromQR ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <span className="font-bold text-blue-900 block">
                {t('cart.dine_in', 'تناول في المكان')} ({t('cart.table_prefix', 'طاولة')} {customerInfo.tableNumber})
              </span>
              <p className="text-xs text-blue-600 mt-0.5">
                {t('cart.auto_selected_qr', 'تم تحديد نوع الطلب تلقائياً من رمز QR')}
              </p>
            </div>
          </div>
        ) : (
          <RadioGroup 
            value={orderType} 
            onValueChange={(value) => setOrderType(value as OrderType)} 
            className="grid grid-cols-2 gap-3"
          >
            <div>
              <RadioGroupItem value="pickup" id="pickup" className="peer sr-only" />
              <Label 
                htmlFor="pickup" 
                className="flex flex-col items-center justify-center p-4 bg-white border-2 border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all h-full"
              >
                <Utensils className="h-6 w-6 mb-2 text-gray-500 peer-data-[state=checked]:text-primary" />
                <span className="font-medium text-gray-900">{t('cart.pickup', 'استلام من المكان')}</span>
              </Label>
            </div>
            <div>
              <RadioGroupItem value="delivery" id="delivery" className="peer sr-only" />
              <Label 
                htmlFor="delivery" 
                className="flex flex-col items-center justify-center p-4 bg-white border-2 border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all h-full"
              >
                <MapPin className="h-6 w-6 mb-2 text-gray-500 peer-data-[state=checked]:text-primary" />
                <span className="font-medium text-gray-900">{t('cart.delivery', 'توصيل')}</span>
              </Label>
            </div>
          </RadioGroup>
        )}
      </div>

      {/* Delivery Zone Selection */}
      {orderType === 'delivery' && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <Label htmlFor="delivery-zone" className="text-sm font-medium">{t('cart.delivery_zone_label', 'منطقة التوصيل')} *</Label>
          {zonesLoading ? (
            <div className="text-sm text-gray-500 p-2 bg-gray-50 rounded-lg">{t('cart.loading_zones', 'جاري تحميل المناطق...')}</div>
          ) : (Array.isArray(deliveryZones) && deliveryZones.length > 0) ? (
            <Select value={selectedDeliveryZone?.id || ''} onValueChange={(value) => {
              const zone = (Array.isArray(deliveryZones) ? deliveryZones : []).find(z => z.id === value);
              setSelectedDeliveryZone(zone || null);
            }}>
              <SelectTrigger className="h-12 rounded-xl border-gray-200">
                <SelectValue placeholder={t('cart.delivery_zone_placeholder', 'اختر منطقة التوصيل')} />
              </SelectTrigger>
              <SelectContent>
                {(Array.isArray(deliveryZones) ? deliveryZones : []).map(zone => (
                  <SelectItem key={zone.id} value={zone.id}>
                    <div className="flex flex-col py-1">
                      <span className="font-medium">{zone.zone_name}</span>
                      <span className="text-xs text-gray-500">
                        {zone.delivery_fee.toFixed(2)} {currency} · {t('cart.minimum_order', 'الحد الأدنى')}: {zone.minimum_order_amount.toFixed(2)} {currency}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm text-red-500 p-2 bg-red-50 rounded-lg">{t('cart.no_delivery_zones', 'لا توجد مناطق توصيل متاحة')}</div>
          )}
          
          {selectedDeliveryZone && (
            <div className="text-xs text-gray-600 space-y-1 bg-gray-50 p-3 rounded-lg mt-2">
              <div className="flex justify-between">
                <span>{t('cart.delivery_fee', 'رسوم التوصيل')}:</span>
                <span className="font-medium">{selectedDeliveryZone.delivery_fee.toFixed(2)} {currency}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('cart.estimated_delivery', 'وقت التوصيل المتوقع')}:</span>
                <span className="font-medium">{selectedDeliveryZone.estimated_delivery_time_minutes} {t('cart.minutes', 'دقيقة')}</span>
              </div>
              {totalAmount < selectedDeliveryZone.minimum_order_amount && (
                <div className="text-red-500 font-medium mt-1 pt-1 border-t border-gray-200">
                  {t('cart.minimum_order', 'الحد الأدنى للطلب')}: {selectedDeliveryZone.minimum_order_amount.toFixed(2)} {currency}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Customer Information */}
      <div className="space-y-4 pt-2">
        <h3 className="font-bold text-gray-900 border-b pb-2">{t('cart.contact_info', 'معلومات التواصل')}</h3>
        
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="customer-name" className="text-sm font-medium">
              {(orderType === 'dine_in' && isTableFromQR) ? t('cart.name_optional', 'الاسم (اختياري)') : `${t('cart.name_label', 'الاسم')} *`}
            </Label>
            <Input 
              id="customer-name" 
              value={customerInfo.name} 
              onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))} 
              placeholder={t('cart.name_placeholder', 'اكتب اسمك الكامل')}
              className="h-12 rounded-xl border-gray-200"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="customer-phone" className="text-sm font-medium">
              {(orderType === 'dine_in' && isTableFromQR) ? t('cart.phone_optional', 'رقم الهاتف (اختياري)') : `${t('cart.phone_label', 'الهاتف')} *`}
            </Label>
            <Input 
              id="customer-phone" 
              value={customerInfo.phone} 
              onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))} 
              placeholder={t('cart.phone_placeholder', 'اكتب رقم هاتفك')}
              className="h-12 rounded-xl border-gray-200 text-right"
              type="tel"
              dir="ltr"
            />
          </div>

          {orderType === 'delivery' && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
              <Label htmlFor="customer-address" className="text-sm font-medium">{t('cart.address_label', 'العنوان')} *</Label>
              <Textarea 
                id="customer-address" 
                value={customerInfo.address} 
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))} 
                placeholder={t('cart.address_placeholder', 'اكتب عنوانك بالتفصيل (اسم الشارع، رقم المبنى، رقم الشقة)')}
                className="min-h-[80px] rounded-xl border-gray-200 resize-none"
              />
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="customer-notes" className="text-sm font-medium">{t('cart.notes_optional', 'ملاحظات (اختياري)')}</Label>
            <Textarea 
              id="customer-notes" 
              value={customerInfo.notes} 
              onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))} 
              placeholder={t('cart.notes_placeholder', 'أي ملاحظات خاصة أو طلبات إضافية')}
              className="min-h-[80px] rounded-xl border-gray-200 resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
